import React, { useEffect, useState, useCallback } from "react";
import { BASE_URL, authFetch } from "../../api/api";
import { useAuth } from "../../auth/AuthContext";
import "../components/AdminLayout.css";

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
};

const USERS_ENDPOINT = `${BASE_URL}/users`;

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [updating, setUpdating] = useState(null); // id of user being updated
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { toast, show } = useToast();

  const load = useCallback(async () => {
    try {
      const data = await authFetch(USERS_ENDPOINT);
      setUsers(data);
    } catch (err) {
      show(err.message || "Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => { load(); }, [load]);

  // ── Toggle role between admin and user ──────────────────
  const toggleRole = async (user) => {
    if (user.id === currentUser?.id) {
      show("You cannot change your own role", "error");
      return;
    }
    const newRole = user.role === "admin" ? "user" : "admin";
    setUpdating(user.id);
    try {
      await authFetch(`${USERS_ENDPOINT}/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({ role: newRole }),
      });
      show(
        newRole === "admin"
          ? `${user.username} is now an Admin ✓`
          : `${user.username} downgraded to User`
      );
      load();
    } catch (err) {
      show(err.message || "Update failed", "error");
    } finally {
      setUpdating(null);
    }
  };

  // ── Delete user ──────────────────────────────────────────
  const deleteUser = async (user) => {
    try {
      await authFetch(`${USERS_ENDPOINT}/${user.id}`, { method: "DELETE" });
      show(`${user.username} deleted.`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      show(err.message || "Delete failed", "error");
    }
  };

  const filtered = users.filter((u) =>
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const admins = filtered.filter((u) => u.role === "admin").length;
  const regular = filtered.filter((u) => u.role !== "admin").length;

  return (
    <div>
      {/* Header */}
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">User Management</h1>
          <p className="adm-page-sub">
            {users.length} total users —{" "}
            <span style={{ color: "#0a9b7c" }}>{admins} admin{admins !== 1 ? "s" : ""}</span>
            {" · "}
            <span style={{ color: "rgba(232,232,232,0.45)" }}>{regular} regular</span>
          </p>
        </div>
      </div>

      {/* Info card */}
      <div style={{
        background: "rgba(10,107,90,0.08)",
        border: "1px solid rgba(10,107,90,0.2)",
        borderRadius: 10,
        padding: "0.875rem 1.1rem",
        marginBottom: "1.25rem",
        display: "flex",
        alignItems: "flex-start",
        gap: "0.75rem",
        fontSize: "0.835rem",
        color: "rgba(232,232,232,0.6)",
        lineHeight: 1.6,
      }}>
        <i className="fa-solid fa-circle-info" style={{ color: "#0a9b7c", marginTop: 2, flexShrink: 0 }}></i>
        <span>
          Toggle the <strong style={{ color: "#e8e8e8" }}>Admin</strong> switch to grant or revoke dashboard access.
          Admins can manage all site content. Regular users have no admin access.
          You cannot change your own role or delete your own account.
        </span>
      </div>

      {/* Search */}
      <div style={{ marginBottom: "1rem", position: "relative", maxWidth: 320 }}>
        <i className="fa-solid fa-magnifying-glass" style={{
          position: "absolute", left: "0.875rem", top: "50%",
          transform: "translateY(-50%)", color: "rgba(255,255,255,0.25)", fontSize: "0.8rem",
        }}></i>
        <input
          className="adm-form-input"
          style={{ paddingLeft: "2.5rem", width: "100%" }}
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="adm-table-wrap">
        {loading ? (
          <div className="adm-loading">
            <div className="adm-loading-spinner"></div>
            Loading users...
          </div>
        ) : filtered.length === 0 ? (
          <div className="adm-empty">
            <i className="fa-solid fa-users" style={{ fontSize: "2rem", color: "rgba(255,255,255,0.1)" }}></i>
            {search ? "No users match your search." : "No users found."}
          </div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Joined</th>
                <th>Role</th>
                <th>Admin Access</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isMe = u.id === currentUser?.id;
                const isAdmin = u.role === "admin";
                const isUpdating = updating === u.id;

                return (
                  <tr key={u.id}>
                    {/* User */}
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                          background: isAdmin
                            ? "linear-gradient(135deg,#0a6b5a,#074a3f)"
                            : "rgba(255,255,255,0.06)",
                          border: isAdmin ? "none" : "1px solid rgba(255,255,255,0.08)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, color: "white", fontSize: "0.875rem",
                        }}>
                          {u.username?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            {u.username}
                            {isMe && (
                              <span style={{
                                fontSize: "0.65rem", padding: "0.1rem 0.4rem",
                                background: "rgba(195,185,150,0.12)", border: "1px solid rgba(195,185,150,0.25)",
                                borderRadius: 999, color: "#c3b996", fontWeight: 600,
                              }}>You</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td style={{ color: "rgba(232,232,232,0.5)", fontSize: "0.825rem" }}>
                      {u.email}
                    </td>

                    {/* Joined */}
                    <td style={{ color: "rgba(232,232,232,0.4)", fontSize: "0.8rem" }}>
                      {u.created_at
                        ? new Date(u.created_at).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })
                        : "—"}
                    </td>

                    {/* Role badge */}
                    <td>
                      {isAdmin
                        ? <span className="adm-badge adm-badge--green">Admin</span>
                        : <span className="adm-badge" style={{ background: "rgba(255,255,255,0.05)", color: "rgba(232,232,232,0.4)", border: "1px solid rgba(255,255,255,0.08)" }}>User</span>
                      }
                    </td>

                    {/* Toggle switch */}
                    <td>
                      <button
                        onClick={() => !isMe && toggleRole(u)}
                        disabled={isMe || isUpdating}
                        title={isMe ? "You cannot change your own role" : isAdmin ? "Revoke admin access" : "Grant admin access"}
                        style={{
                          width: 44, height: 24,
                          borderRadius: 999,
                          border: "none",
                          background: isAdmin ? "#0a6b5a" : "rgba(255,255,255,0.1)",
                          cursor: isMe ? "not-allowed" : "pointer",
                          position: "relative",
                          transition: "background 0.25s",
                          opacity: isMe ? 0.4 : 1,
                          flexShrink: 0,
                        }}
                      >
                        <div style={{
                          position: "absolute",
                          top: 3,
                          left: isAdmin ? 23 : 3,
                          width: 18, height: 18,
                          borderRadius: "50%",
                          background: "white",
                          transition: "left 0.25s",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {isUpdating && (
                            <div style={{
                              width: 10, height: 10,
                              border: "1.5px solid rgba(10,107,90,0.3)",
                              borderTopColor: "#0a6b5a",
                              borderRadius: "50%",
                              animation: "adm-spin 0.6s linear infinite",
                            }} />
                          )}
                        </div>
                      </button>
                    </td>

                    {/* Delete */}
                    <td>
                      <button
                        className="adm-delete-btn"
                        onClick={() => !isMe && setConfirmDelete(u)}
                        disabled={isMe}
                        style={{ opacity: isMe ? 0.3 : 1, cursor: isMe ? "not-allowed" : "pointer" }}
                        title={isMe ? "You cannot delete your own account" : "Delete user"}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="adm-modal-backdrop" onClick={() => setConfirmDelete(null)}>
          <div className="adm-modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3 className="adm-modal-title">Delete User</h3>
              <button className="adm-modal-close" onClick={() => setConfirmDelete(null)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="adm-modal-body">
              <p style={{ color: "rgba(232,232,232,0.6)", fontSize: "0.9rem", lineHeight: 1.6 }}>
                Are you sure you want to delete{" "}
                <strong style={{ color: "white" }}>{confirmDelete.username}</strong>{" "}
                ({confirmDelete.email})? This cannot be undone.
              </p>
            </div>
            <div className="adm-modal-footer">
              <button className="adm-cancel-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button
                className="adm-save-btn"
                style={{ background: "linear-gradient(135deg,#b91c1c,#7f1d1d)" }}
                onClick={() => deleteUser(confirmDelete)}
              >
                <i className="fa-solid fa-trash"></i> Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
